import { ethers } from 'ethers';
import { CONTRACT_ADDRESSES, SUPPORTED_NETWORKS, GAS_LIMITS, ERROR_MESSAGES } from '../utils/constants';

// ABI for the deployed contract (key functions only)
const QUANTUM_VAULT_ABI = [
  // Read functions
  "function registeredSafes(address) view returns (bool)",
  "function getSafeVaults(address safeAddress) view returns (uint256[])",
  "function proposals(uint256) view returns (uint256 id, address safeAddress, address proposer, string operationType, uint256 requiredSignatures, uint256 currentSignatures, uint256 timestamp, uint256 expirationTime, bool executed)",
  "function getProposalAssets(uint256 proposalId) view returns (string[])",
  "function hasApprovedProposal(uint256 proposalId, address approver) view returns (bool)",
  "function encryptedAssets(uint256) view returns (address safeAddress, string assetId, bytes32 encryptionHash, uint256 timestamp, uint256 expirationTime, bool isActive, address encryptedBy)",

  // Write functions
  "function registerSafe(address safeAddress)",
  "function createProposal(address safeAddress, string operationType, string[] assetIds, uint8 securityLevel, uint256 requiredSignatures, uint256 expirationDuration) returns (uint256)",
  "function approveProposal(uint256 proposalId)",
  "function executeEncryption(uint256 proposalId, bytes32[] encryptionHashes)",
  "function executeDecryption(uint256 proposalId, uint256[] vaultIds)",

  // Events
  "event SafeRegistered(address indexed safeAddress, uint256 timestamp)",
  "event AssetEncrypted(uint256 indexed vaultId, address indexed safeAddress, string assetId, bytes32 encryptionHash, uint8 securityLevel, uint256 timestamp)",
  "event AssetDecrypted(uint256 indexed vaultId, address indexed safeAddress, string assetId, uint256 timestamp)",
  "event ProposalCreated(uint256 indexed proposalId, address indexed safeAddress, address proposer, string operationType, uint256 requiredSignatures)",
  "event ProposalApproved(uint256 indexed proposalId, address indexed approver, uint256 currentSignatures, uint256 requiredSignatures)",
  "event ProposalExecuted(uint256 indexed proposalId, address indexed executor, bool success)"
];

export class ContractService {
  private contract: ethers.Contract | null = null;
  private provider: ethers.Provider | null = null;
  private signer: ethers.Signer | null = null;
  private chainId: number | null = null;

  constructor() {
    this.initializeProvider();
  }

  private async initializeProvider() {
    if (typeof window !== 'undefined' && (window as any).ethereum) {
      this.provider = new ethers.BrowserProvider((window as any).ethereum);

      // Get network info
      const network = await this.provider.getNetwork();
      this.chainId = Number(network.chainId);

      // Initialize contract
      this.initializeContract();
    }
  }

  private initializeContract() {
    if (!this.provider || !this.chainId) return;

    const networkName = SUPPORTED_NETWORKS[this.chainId as keyof typeof SUPPORTED_NETWORKS];
    const contractAddress = CONTRACT_ADDRESSES[networkName as keyof typeof CONTRACT_ADDRESSES];

    if (!contractAddress) {
      console.warn(`Contract not deployed on network ${networkName} (${this.chainId})`);
      return;
    }

    this.contract = new ethers.Contract(contractAddress, QUANTUM_VAULT_ABI, this.provider);
  }

  async connectWallet(): Promise<ethers.Signer> {
    if (!this.provider) {
      throw new Error('No provider available');
    }

    // Use BrowserProvider instead of generic Provider
    const browserProvider = this.provider as ethers.BrowserProvider;
    this.signer = await browserProvider.getSigner();

    // Re-initialize contract with signer
    if (this.contract && this.signer) {
      this.contract = this.contract.connect(this.signer) as ethers.Contract;
    }

    return this.signer;
  }

  getContractAddress(): string | null {
    if (!this.chainId) return null;

    const networkName = SUPPORTED_NETWORKS[this.chainId as keyof typeof SUPPORTED_NETWORKS];
    return CONTRACT_ADDRESSES[networkName as keyof typeof CONTRACT_ADDRESSES] || null;
  }

  getContract(): ethers.Contract {
    if (!this.contract) {
      throw new Error(ERROR_MESSAGES.CONTRACT_NOT_FOUND);
    }
    return this.contract;
  }

  // Safe Registration
  async isSafeRegistered(safeAddress: string): Promise<boolean> {
    const contract = this.getContract();
    return await contract.registeredSafes(safeAddress);
  }

  async registerSafe(safeAddress: string): Promise<ethers.TransactionResponse> {
    if (!this.signer) {
      throw new Error(ERROR_MESSAGES.WALLET_NOT_CONNECTED);
    }

    const contract = this.getContract();
    return await contract.registerSafe(safeAddress, {
      gasLimit: GAS_LIMITS.registerSafe
    });
  }

  // Proposal Management
  async createProposal(
    safeAddress: string,
    operationType: 'encrypt' | 'decrypt',
    assetIds: string[],
    securityLevel: number,
    requiredSignatures: number,
    expirationDuration: number = 7 * 24 * 60 * 60 // 7 days
  ): Promise<ethers.TransactionResponse> {
    if (!this.signer) {
      throw new Error(ERROR_MESSAGES.WALLET_NOT_CONNECTED);
    }

    const contract = this.getContract();
    return await contract.createProposal(
      safeAddress,
      operationType,
      assetIds,
      securityLevel,
      requiredSignatures,
      expirationDuration,
      {
        gasLimit: GAS_LIMITS.createProposal
      }
    );
  }

  async approveProposal(proposalId: number): Promise<ethers.TransactionResponse> {
    if (!this.signer) {
      throw new Error(ERROR_MESSAGES.WALLET_NOT_CONNECTED);
    }

    const contract = this.getContract();
    return await contract.approveProposal(proposalId, {
      gasLimit: GAS_LIMITS.approveProposal
    });
  }

  async getProposal(proposalId: number) {
    const contract = this.getContract();
    const proposal = await contract.proposals(proposalId);
    const assetIds = await contract.getProposalAssets(proposalId);

    return {
      id: proposal.id,
      safeAddress: proposal.safeAddress,
      proposer: proposal.proposer,
      operationType: proposal.operationType,
      requiredSignatures: proposal.requiredSignatures,
      currentSignatures: proposal.currentSignatures,
      timestamp: proposal.timestamp,
      expirationTime: proposal.expirationTime,
      executed: proposal.executed,
      assetIds: assetIds
    };
  }

  async hasApprovedProposal(proposalId: number, approver: string): Promise<boolean> {
    const contract = this.getContract();
    return await contract.hasApprovedProposal(proposalId, approver);
  }

  // Encryption/Decryption
  async executeEncryption(
    proposalId: number,
    encryptionHashes: string[]
  ): Promise<ethers.TransactionResponse> {
    if (!this.signer) {
      throw new Error(ERROR_MESSAGES.WALLET_NOT_CONNECTED);
    }

    const contract = this.getContract();
    return await contract.executeEncryption(proposalId, encryptionHashes, {
      gasLimit: GAS_LIMITS.executeEncryption
    });
  }

  async executeDecryption(
    proposalId: number,
    vaultIds: number[]
  ): Promise<ethers.TransactionResponse> {
    if (!this.signer) {
      throw new Error(ERROR_MESSAGES.WALLET_NOT_CONNECTED);
    }

    const contract = this.getContract();
    return await contract.executeDecryption(proposalId, vaultIds, {
      gasLimit: GAS_LIMITS.executeDecryption
    });
  }

  // Vault Management
  async getSafeVaults(safeAddress: string): Promise<number[]> {
    const contract = this.getContract();
    const vaultIds = await contract.getSafeVaults(safeAddress);
    return vaultIds.map((id: any) => Number(id));
  }

  async getEncryptedAsset(vaultId: number) {
    const contract = this.getContract();
    const asset = await contract.encryptedAssets(vaultId);

    return {
      safeAddress: asset.safeAddress,
      assetId: asset.assetId,
      encryptionHash: asset.encryptionHash,
      timestamp: asset.timestamp,
      expirationTime: asset.expirationTime,
      isActive: asset.isActive,
      encryptedBy: asset.encryptedBy
    };
  }

  // Event Listeners
  onSafeRegistered(callback: (safeAddress: string, timestamp: number) => void) {
    const contract = this.getContract();
    contract.on('SafeRegistered', callback);
  }

  onAssetEncrypted(callback: (vaultId: number, safeAddress: string, assetId: string, encryptionHash: string, securityLevel: number, timestamp: number) => void) {
    const contract = this.getContract();
    contract.on('AssetEncrypted', callback);
  }

  onProposalCreated(callback: (proposalId: number, safeAddress: string, proposer: string, operationType: string, requiredSignatures: number) => void) {
    const contract = this.getContract();
    contract.on('ProposalCreated', callback);
  }

  onProposalApproved(callback: (proposalId: number, approver: string, currentSignatures: number, requiredSignatures: number) => void) {
    const contract = this.getContract();
    contract.on('ProposalApproved', callback);
  }

  // Utility Methods
  async getCurrentAccount(): Promise<string | null> {
    if (!this.signer) return null;
    try {
      return await this.signer.getAddress();
    } catch (error) {
      console.error('Failed to get current account:', error);
      return null;
    }
  }

  async getBalance(address: string): Promise<string> {
    if (!this.provider) throw new Error('No provider available');
    const balance = await this.provider.getBalance(address);
    return ethers.formatEther(balance);
  }

  isContractAvailable(): boolean {
    return this.contract !== null;
  }

  getCurrentNetwork(): string | null {
    if (!this.chainId) return null;
    return SUPPORTED_NETWORKS[this.chainId as keyof typeof SUPPORTED_NETWORKS] || null;
  }
}

// Singleton instance
export const contractService = new ContractService();